import { styled } from '@mui/material';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { WBBox, WBDivider } from '@admiin-com/ds-web';

export const StyledEditorContainer = styled(WBBox)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  minHeight: 400,
}));

export const StyledToolbarContainer = styled(WBBox)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const StyledToolbar = styled(WBBox)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.75),
  overflowX: 'auto',
  whiteSpace: 'nowrap',
  color: theme.palette.text.primary,
  fontSize: '14px',
  minHeight: 48,
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: theme.spacing(1.5),
    pointerEvents: 'none',
    transition: 'opacity 0.2s',
    zIndex: 1,
  },
  '&::before': {
    left: 0,
    boxShadow: 'inset 8px 0 8px -8px rgba(0,0,0,0.2)',
    opacity: 0,
  },
  '&::after': {
    right: 0,
    boxShadow: 'inset -8px 0 8px -8px rgba(0,0,0,0.2)',
    opacity: 0,
  },
  '&.show-left-shadow::before': {
    opacity: 1,
  },
  '&.show-right-shadow::after': {
    opacity: 1,
  },
  '& .MuiIconButton-root': {
    fontSize: '14px',
    padding: theme.spacing(0.5),
    minWidth: 32,
    height: 32,
    '& span': {
      fontSize: '14px',
    },
  },
  '& .MuiButton-root': {
    fontSize: '14px',
    minWidth: 'auto',
    padding: theme.spacing(0.5, 0.75),
    height: 32,
  },
  '& .MuiTextField-root': {
    '& .MuiInputBase-input': {
      fontSize: '14px',
      padding: theme.spacing(0.5),
      height: 'auto',
    },
    '& .MuiInputBase-root': {
      height: 32,
    },
  },
  '& .MuiMenu-paper': {
    fontSize: '12px',
  },
  '& .MuiMenuItem-root': {
    fontSize: '12px',
    minHeight: 32,
    padding: theme.spacing(0.5, 1),
    '& span': {
      fontSize: '12px',
    },
  },
}));

/**
 * StyledContentEditable applies the document-content styles for WYSIWYG consistency.
 *
 * The key properties (white-space: pre-wrap, line-height: 1.5, word-break: break-word)
 * are shared with DocumentPreviewModal and DocumentRenderer via document-content.css
 * to ensure what users see while editing matches the final preview/PDF output.
 *
 * Uses hardcoded values (not theme values) to match document-content.css and ensure
 * consistency with backend PDF generation via Puppeteer.
 */
export const StyledContentEditable = styled(ContentEditable)(({ theme }) => ({
  // Match the generated HTML padding used in document preview/generation
  // Top/bottom padding is 0 to allow letterhead space, left/right match document margins
  padding: '0px 40px 0px 40px',
  outline: 'none',
  // Document content shared styles for WYSIWYG consistency
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  lineHeight: 1.5,
  fontSize: '11px',
  color: theme.palette.text.primary,
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  '& h1': {
    fontSize: '28px',
    fontWeight: 700,
    margin: '16px 0',
    lineHeight: 1.3,
  },
  '& h2': {
    fontSize: '24px',
    fontWeight: 700,
    margin: '14px 0',
    lineHeight: 1.3,
  },
  '& h3': {
    fontSize: '20px',
    fontWeight: 700,
    margin: '12px 0',
    lineHeight: 1.3,
  },
  '& p': {
    margin: '0px',
  },
  // Preserve empty paragraphs (newlines) for WYSIWYG
  '& p:empty::before': {
    content: '"\\00a0"',
    visibility: 'hidden',
  },
  '& blockquote': {
    borderLeft: '4px solid #e0e0e0',
    paddingLeft: '16px',
    marginLeft: 0,
    color: 'rgba(0, 0, 0, 0.6)',
    fontStyle: 'italic',
  },
  '& ul, & ol': {
    marginLeft: '24px',
    margin: '8px 0',
  },
  '& li': {
    margin: '4px 0',
  },
  '& a': {
    color: '#1976d2',
    textDecoration: 'underline',
  },
  '& table': {
    borderCollapse: 'collapse',
    borderSpacing: 0,
    width: '100%',
  },
  '& table td, & table th': {
    border: '1px solid #e0e0e0',
    padding: '8px',
    textAlign: 'left',
    minWidth: 100,
    backgroundColor: 'transparent !important',
  },
  '& table p': {
    margin: 0,
  },
  '& .columns': {
    display: 'flex',
    width: '100%',
  },
  '& .columns > div': {
    flex: 1,
    padding: '8px',
  },
  '& th': {
    backgroundColor: 'transparent',
    fontWeight: 400,
  },
  '& code': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    padding: '16px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '14px',
    display: 'block',
    margin: '16px 0',
    whiteSpace: 'pre-wrap',
    overflow: 'auto',
  },
  '& .code': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    padding: '16px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '14px',
    display: 'block',
    margin: '16px 0',
    whiteSpace: 'pre-wrap',
    overflow: 'auto',
  },
  '& .editor-text-bold': {
    fontWeight: 'bold',
  },
  '& .editor-text-italic': {
    fontStyle: 'italic',
  },
  '& .editor-text-underline': {
    textDecoration: 'underline',
  },
  '& .editor-text-strikethrough': {
    textDecoration: 'line-through',
  },
  '& .editor-text-subscript': {
    fontSize: '0.8em',
    verticalAlign: 'sub',
  },
  '& .handlebars-property': {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    padding: '2px 4px',
    borderRadius: '3px',
    fontFamily: 'monospace',
    fontWeight: 500,
    border: '1px solid #bbdefb',
    display: 'inline-block',
  },
}));

export const StyledPlaceholder = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(5),
  color: theme.palette.text.secondary,
  pointerEvents: 'none',
  userSelect: 'none',
}));

export const VerticalDivider = styled(WBDivider)(({ theme }) => ({
  height: 'auto',
  marginLeft: theme.spacing(0.5),
  marginRight: theme.spacing(0.5),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));
